// SPDX-License-Identifier: AGPL-3.0-or-later

import Accessibility from '@app/features/accessibility/state/Accessibility';
import styles from '@app/features/ui/components/Scroller.module.css';
import FocusRingScope from '@app/features/ui/focus_ring/FocusRingScope';
import {ScrollerTrack} from '@app/features/ui/scroller/ScrollerTrack';
import {useScrollerThumb} from '@app/features/ui/scroller/useScrollerThumb';
import {
	canUseWindowFocusedHoverControls,
	subscribeWindowHoverControlsChange,
} from '@app/features/ui/utils/WindowFocusInteractionGuard';
import {clsx} from 'clsx';
import {
	type CSSProperties,
	forwardRef,
	type HTMLAttributes,
	type KeyboardEvent as ReactKeyboardEvent,
	type MouseEvent as ReactMouseEvent,
	type ReactNode,
	type PointerEvent as ReactPointerEvent,
	type TouchEvent as ReactTouchEvent,
	type WheelEvent as ReactWheelEvent,
	type UIEvent,
	useCallback,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from 'react';
export interface ScrollerState {
	scrollTop: number;
	scrollHeight: number;
	offsetHeight: number;
	scrollLeft: number;
	scrollWidth: number;
	offsetWidth: number;
}
export interface ScrollerHandle {
	getScrollerNode(): HTMLElement | null;
	getScrollerState(): ScrollerState;
	scrollTo(options: {to: number; animate?: boolean; callback?: () => void}): void;
	mergeTo(options: {to: number; callback?: () => void}): void;
	scrollIntoViewRect(options: {
		start: number;
		end: number;
		shouldScrollToStart?: boolean;
		padding?: number;
		animate?: boolean;
		callback?: () => void;
	}): void;
	scrollIntoViewNode(options: {
		node: HTMLElement;
		shouldScrollToStart?: boolean;
		padding?: number;
		animate?: boolean;
		callback?: () => void;
	}): void;
	scrollPageUp(options?: {animate?: boolean; callback?: () => void}): void;
	scrollPageDown(options?: {animate?: boolean; callback?: () => void}): void;
	scrollToTop(options?: {animate?: boolean; callback?: () => void}): void;
	scrollToBottom(options?: {animate?: boolean; callback?: () => void}): void;
	isScrolledToTop(): boolean;
	isScrolledToBottom(): boolean;
	getDistanceFromTop(): number;
	getDistanceFromBottom(): number;
}

type ScrollAxis = 'vertical' | 'horizontal';

type ScrollOverflow = 'scroll' | 'auto' | 'hidden';

type ScrollbarTrackMode = 'overlay' | 'reserve';
interface ScrollerProps
	extends Omit<
		HTMLAttributes<HTMLDivElement>,
		'children' | 'className' | 'style' | 'onScroll' | 'onKeyDown' | 'onMouseLeave' | 'onTouchStart' | 'onWheel'
	> {
	children: ReactNode;
	className?: string;
	contentClassName?: string;
	dir?: 'ltr' | 'rtl';
	orientation?: ScrollAxis;
	overflow?: ScrollOverflow;
	fade?: boolean;
	scrollbar?: 'thin' | 'regular';
	showTrack?: boolean;
	hideThumbWhenWindowBlurred?: boolean;
	scrollbarTrackMode?: ScrollbarTrackMode;
	style?: CSSProperties;
	onScroll?: (event: UIEvent<HTMLDivElement>) => void;
	onKeyDown?: (event: ReactKeyboardEvent<HTMLDivElement>) => void;
	onMouseLeave?: (event: ReactMouseEvent<HTMLDivElement>) => void;
	onTouchStart?: (event: ReactTouchEvent<HTMLDivElement>) => void;
	onWheel?: (event: ReactWheelEvent<HTMLDivElement>) => void;
	onScrollIntent?: () => void;
	onResize?: (entry: ResizeObserverEntry, type: 'container' | 'content') => void;
}
function getScrollState(element: HTMLElement | null): ScrollerState {
	if (!element) {
		return {
			scrollTop: 0,
			scrollHeight: 0,
			offsetHeight: 0,
			scrollLeft: 0,
			scrollWidth: 0,
			offsetWidth: 0,
		};
	}
	return {
		scrollTop: element.scrollTop,
		scrollHeight: element.scrollHeight,
		offsetHeight: element.offsetHeight,
		scrollLeft: element.scrollLeft,
		scrollWidth: element.scrollWidth,
		offsetWidth: element.offsetWidth,
	};
}
function measureElementOffset(element: HTMLElement, axis: ScrollAxis, container: HTMLElement): number {
	const elementRect = element.getBoundingClientRect();
	const containerRect = container.getBoundingClientRect();
	const scrollPos = axis === 'horizontal' ? container.scrollLeft : container.scrollTop;
	const delta = axis === 'horizontal' ? elementRect.left - containerRect.left : elementRect.top - containerRect.top;
	return scrollPos + delta;
}
interface SpringConfig {
	stiffness: number;
	damping: number;
	mass: number;
	precision: number;
	maxDurationMs: number;
}
const DEFAULT_SPRING: SpringConfig = {
	stiffness: 520,
	damping: 70,
	mass: 1,
	precision: 0.5,
	maxDurationMs: 1400,
};
const MIDDLE_MOUSE_BUTTON = 1;
const AUTOSCROLL_DEAD_ZONE_PX = 8;
const AUTOSCROLL_HOLD_RELEASE_THRESHOLD_PX = AUTOSCROLL_DEAD_ZONE_PX;
const AUTOSCROLL_MAX_SPEED_PX_PER_SECOND = 6000;
const AUTOSCROLL_REFERENCE_DISTANCE_PX = 250;

const AUTOSCROLL_NATIVE_DEFAULT_TARGET_SELECTOR = [
	'a[href]',
	'input',
	'textarea',
	'select',
	'[contenteditable=""]',
	'[contenteditable="true"]',
	'[data-middle-click-autoscroll-ignore="true"]',
].join(',');
const AUTOSCROLL_IGNORED_TARGET_SELECTOR = [
	AUTOSCROLL_NATIVE_DEFAULT_TARGET_SELECTOR,
	'button',
	'[role="button"]',
].join(',');
const KEYBOARD_SCROLL_KEYS = new Set([
	'ArrowDown',
	'ArrowLeft',
	'ArrowRight',
	'ArrowUp',
	'End',
	'Home',
	'PageDown',
	'PageUp',
	' ',
	'Space',
	'Spacebar',
]);
interface MiddleMouseAutoscrollState {
	node: HTMLDivElement;
	originClientX: number;
	originClientY: number;
	lastClientX: number;
	lastClientY: number;
	lastFrameTime: number;
	rafId: number | null;
	isMiddleButtonDown: boolean;
	stopOnMiddleMouseUp: boolean;
	cleanup?: () => void;
}
interface MiddleMouseStopEvent {
	button: number;
	clientX: number;
	clientY: number;
	timeStamp: number;
}
function prefersReducedMotion(): boolean {
	return Accessibility.useReducedMotion;
}
function setMiddleMouseAutoscrollCursor(orientation: ScrollAxis | null): void {
	if (orientation === null) {
		delete document.documentElement.dataset.middleMouseAutoscroll;
		return;
	}
	document.documentElement.dataset.middleMouseAutoscroll = orientation;
}
function getMiddleMouseStopEvent(event: MouseEvent): MiddleMouseStopEvent {
	return {
		button: event.button,
		clientX: event.clientX,
		clientY: event.clientY,
		timeStamp: event.timeStamp,
	};
}
function isSameMouseDownEvent(a: MiddleMouseStopEvent | null, b: MouseEvent): boolean {
	return (
		a !== null &&
		a.button === b.button &&
		a.clientX === b.clientX &&
		a.clientY === b.clientY &&
		Math.abs(a.timeStamp - b.timeStamp) < 1
	);
}
function targetMatches(target: EventTarget | null, selector: string): boolean {
	return target instanceof Element && target.closest(selector) !== null;
}
function shouldTreatKeyAsScrollIntent(event: ReactKeyboardEvent<HTMLDivElement>): boolean {
	if (!KEYBOARD_SCROLL_KEYS.has(event.key)) {
		return false;
	}
	return !targetMatches(event.target, AUTOSCROLL_IGNORED_TARGET_SELECTOR);
}
function getAutoscrollVelocity(distancePx: number): number {
	const distancePastDeadZone = Math.max(0, Math.abs(distancePx) - AUTOSCROLL_DEAD_ZONE_PX);
	if (distancePastDeadZone === 0) {
		return 0;
	}
	const normalized = Math.min(1, distancePastDeadZone / AUTOSCROLL_REFERENCE_DISTANCE_PX);
	const speed = normalized * normalized * AUTOSCROLL_MAX_SPEED_PX_PER_SECOND;
	return Math.sign(distancePx) * speed;
}
export const Scroller = forwardRef<ScrollerHandle, ScrollerProps>(function Scroller(
	{
		children,
		className,
		contentClassName,
		dir = 'ltr',
		orientation = 'vertical',
		overflow = 'auto',
		fade = true,
		scrollbar = 'thin',
		showTrack = true,
		hideThumbWhenWindowBlurred = false,
		scrollbarTrackMode = 'overlay',
		style,
		onScroll,
		onKeyDown,
		onMouseDown,
		onMouseLeave,
		onTouchStart,
		onWheel,
		onScrollIntent,
		onResize,
		...rest
	},
	forwardedRef,
) {
	const scrollRef = useRef<HTMLDivElement>(null);
	const contentRef = useRef<HTMLDivElement>(null);
	const rootRef = useRef<HTMLDivElement>(null);
	const scrollTimeoutRef = useRef<number>(0);
	const thumbRefreshRafRef = useRef<number | null>(null);
	const middleMouseAutoscrollRef = useRef<MiddleMouseAutoscrollState | null>(null);
	const lastMiddleMouseAutoscrollStopEventRef = useRef<MiddleMouseStopEvent | null>(null);
	const onResizeRef = useRef(onResize);
	const [isHovered, setIsHovered] = useState(false);
	const [isScrolling, setIsScrolling] = useState(false);
	const [isWindowBlurred, setIsWindowBlurred] = useState(false);
	const minThumbSize = scrollbar === 'regular' ? 40 : 24;
	const {isDragging, refreshThumbState, thumbState, onThumbPointerDown, onTrackPointerDown} = useScrollerThumb({
		orientation,
		overflow,
		minThumbSize,
		scrollRef,
	});
	onResizeRef.current = onResize;
	const animRef = useRef<{
		token: number;
		rafId: number | null;
		velocity: number;
		lastTime: number;
		startTime: number;
		target: number;
		callback?: () => void;
	} | null>(null);
	const cancelAnimation = useCallback(() => {
		const anim = animRef.current;
		if (!anim) return;
		if (anim.rafId != null) {
			cancelAnimationFrame(anim.rafId);
		}
		animRef.current = null;
	}, []);
	const cancelProgrammaticScrollForUserIntent = useCallback(() => {
		cancelAnimation();
		onScrollIntent?.();
	}, [cancelAnimation, onScrollIntent]);
	const stopMiddleMouseAutoscroll = useCallback(() => {
		const state = middleMouseAutoscrollRef.current;
		if (!state) return;
		middleMouseAutoscrollRef.current = null;
		if (state.rafId != null) {
			cancelAnimationFrame(state.rafId);
		}
		setMiddleMouseAutoscrollCursor(null);
		state.cleanup?.();
	}, []);
	const cancelScheduledThumbRefresh = useCallback(() => {
		if (thumbRefreshRafRef.current != null) {
			cancelAnimationFrame(thumbRefreshRafRef.current);
			thumbRefreshRafRef.current = null;
		}
	}, []);
	const scheduleThumbRefresh = useCallback(() => {
		if (thumbRefreshRafRef.current != null) {
			return;
		}
		thumbRefreshRafRef.current = requestAnimationFrame(() => {
			thumbRefreshRafRef.current = null;
			refreshThumbState();
		});
	}, [refreshThumbState]);
	const getMaxScroll = useCallback(
		(node: HTMLDivElement): number => {
			if (orientation === 'vertical') {
				return Math.max(0, node.scrollHeight - node.offsetHeight);
			}
			return Math.max(0, node.scrollWidth - node.offsetWidth);
		},
		[orientation],
	);
	const getScrollPos = useCallback(
		(node: HTMLDivElement): number => (orientation === 'vertical' ? node.scrollTop : node.scrollLeft),
		[orientation],
	);
	const setScrollPos = useCallback(
		(node: HTMLDivElement, value: number) => {
			if (orientation === 'vertical') {
				node.scrollTop = value;
			} else {
				node.scrollLeft = value;
			}
		},
		[orientation],
	);
	const clampToRange = useCallback(
		(node: HTMLDivElement, value: number): number => {
			const max = getMaxScroll(node);
			return Math.max(0, Math.min(value, max));
		},
		[getMaxScroll],
	);
	const springScrollTo = useCallback(
		(node: HTMLDivElement, target: number, callback?: () => void, config: SpringConfig = DEFAULT_SPRING) => {
			cancelAnimation();
			const token = (animRef.current?.token ?? 0) + 1;
			const now = performance.now();
			animRef.current = {
				token,
				rafId: null,
				velocity: 0,
				lastTime: now,
				startTime: now,
				target: clampToRange(node, target),
				callback,
			};
			const step = (t: number) => {
				const anim = animRef.current;
				if (!anim || anim.token !== token) return;
				anim.target = clampToRange(node, anim.target);
				const dtRaw = (t - anim.lastTime) / 1000;
				const dt = Math.max(0, Math.min(0.04, dtRaw));
				anim.lastTime = t;
				const x = getScrollPos(node);
				const displacement = anim.target - x;
				const a = (config.stiffness * displacement - config.damping * anim.velocity) / config.mass;
				anim.velocity += a * dt;
				let next = x + anim.velocity * dt;
				next = clampToRange(node, next);
				setScrollPos(node, next);
				const elapsed = t - anim.startTime;
				const settled = Math.abs(displacement) <= config.precision && Math.abs(anim.velocity) <= config.precision;
				const timedOut = elapsed >= config.maxDurationMs;
				if (settled || timedOut) {
					setScrollPos(node, clampToRange(node, anim.target));
					const cb = anim.callback;
					animRef.current = null;
					cb?.();
					return;
				}
				anim.rafId = requestAnimationFrame(step);
			};
			animRef.current.rafId = requestAnimationFrame(step);
		},
		[cancelAnimation, clampToRange, getScrollPos, setScrollPos],
	);
	useEffect(() => {
		const containerEl = rootRef.current;
		const contentEl = contentRef.current;
		if (!containerEl || !contentEl) return;
		const containerObserver = new ResizeObserver((entries) => {
			scheduleThumbRefresh();
			for (const entry of entries) {
				onResizeRef.current?.(entry, 'container');
			}
		});
		const contentObserver = new ResizeObserver((entries) => {
			scheduleThumbRefresh();
			for (const entry of entries) {
				onResizeRef.current?.(entry, 'content');
			}
		});
		containerObserver.observe(containerEl);
		contentObserver.observe(contentEl);
		refreshThumbState();
		return () => {
			cancelScheduledThumbRefresh();
			containerObserver.disconnect();
			contentObserver.disconnect();
		};
	}, [cancelScheduledThumbRefresh, refreshThumbState, scheduleThumbRefresh]);
	useEffect(() => {
		const contentEl = contentRef.current;
		if (!contentEl) {
			return;
		}
		const observer = new MutationObserver(() => {
			scheduleThumbRefresh();
		});
		observer.observe(contentEl, {
			childList: true,
			subtree: true,
			characterData: true,
		});
		return () => {
			observer.disconnect();
		};
	}, [scheduleThumbRefresh]);
	useEffect(() => {
		refreshThumbState();
		scheduleThumbRefresh();
		return cancelScheduledThumbRefresh;
	}, [cancelScheduledThumbRefresh, refreshThumbState, scheduleThumbRefresh]);
	useEffect(() => {
		return () => cancelAnimation();
	}, [cancelAnimation]);
	useEffect(() => {
		return () => stopMiddleMouseAutoscroll();
	}, [stopMiddleMouseAutoscroll]);
	useEffect(() => {
		return () => {
			window.clearTimeout(scrollTimeoutRef.current);
		};
	}, []);
	useEffect(() => {
		return cancelScheduledThumbRefresh;
	}, [cancelScheduledThumbRefresh]);
	useEffect(() => {
		if (!hideThumbWhenWindowBlurred) {
			setIsWindowBlurred(false);
			return;
		}
		const update = () => setIsWindowBlurred(!document.hasFocus());
		update();
		window.addEventListener('blur', update);
		window.addEventListener('focus', update);
		document.addEventListener('visibilitychange', update);
		return () => {
			window.removeEventListener('blur', update);
			window.removeEventListener('focus', update);
			document.removeEventListener('visibilitychange', update);
		};
	}, [hideThumbWhenWindowBlurred]);
	useImperativeHandle(
		forwardedRef,
		() => ({
			getScrollerNode: () => scrollRef.current,
			getScrollerState: () => getScrollState(scrollRef.current),
			scrollTo({to, animate = false, callback}) {
				const node = scrollRef.current;
				if (!node) {
					callback?.();
					return;
				}
				const clampedTo = clampToRange(node, to);
				if (!animate || prefersReducedMotion()) {
					cancelAnimation();
					setScrollPos(node, clampedTo);
					callback?.();
					return;
				}
				springScrollTo(node, clampedTo, callback);
			},
			mergeTo({to, callback}) {
				const node = scrollRef.current;
				if (!node) {
					callback?.();
					return;
				}
				cancelAnimation();
				setScrollPos(node, clampToRange(node, to));
				callback?.();
			},
			scrollIntoViewRect({start, end, shouldScrollToStart = false, padding = 0, animate = false, callback}) {
				const node = scrollRef.current;
				if (!node) {
					callback?.();
					return;
				}
				const scrollPos = getScrollPos(node);
				const viewportSize = orientation === 'vertical' ? node.offsetHeight : node.offsetWidth;
				const paddedStart = start - padding;
				const paddedEnd = end + padding;
				const visibleEnd = scrollPos + viewportSize;
				if (paddedStart >= scrollPos && paddedEnd <= visibleEnd) {
					callback?.();
					return;
				}
				let targetScroll: number;
				if (paddedStart < scrollPos || shouldScrollToStart) {
					targetScroll = paddedStart;
				} else {
					targetScroll = paddedEnd - viewportSize;
				}
				this.scrollTo({to: targetScroll, animate, callback});
			},
			scrollIntoViewNode({node: targetNode, shouldScrollToStart = false, padding = 0, animate = false, callback}) {
				const container = scrollRef.current;
				if (!container) {
					callback?.();
					return;
				}
				const offset = measureElementOffset(targetNode, orientation, container);
				const size = orientation === 'vertical' ? targetNode.offsetHeight : targetNode.offsetWidth;
				this.scrollIntoViewRect({
					start: offset,
					end: offset + size,
					shouldScrollToStart,
					padding,
					animate,
					callback,
				});
			},
			scrollPageUp({animate = false, callback} = {}) {
				const node = scrollRef.current;
				if (!node) {
					callback?.();
					return;
				}
				const scrollPos = getScrollPos(node);
				const viewportSize = orientation === 'vertical' ? node.offsetHeight : node.offsetWidth;
				const target = Math.max(0, scrollPos - 0.9 * viewportSize);
				this.scrollTo({to: target, animate, callback});
			},
			scrollPageDown({animate = false, callback} = {}) {
				const node = scrollRef.current;
				if (!node) {
					callback?.();
					return;
				}
				const scrollPos = getScrollPos(node);
				const viewportSize = orientation === 'vertical' ? node.offsetHeight : node.offsetWidth;
				const maxScroll = getMaxScroll(node);
				const target = Math.min(maxScroll, scrollPos + 0.9 * viewportSize);
				this.scrollTo({to: target, animate, callback});
			},
			scrollToTop({animate = false, callback} = {}) {
				this.scrollTo({to: 0, animate, callback});
			},
			scrollToBottom({animate = false, callback} = {}) {
				const node = scrollRef.current;
				if (!node) {
					callback?.();
					return;
				}
				this.scrollTo({to: getMaxScroll(node), animate, callback});
			},
			isScrolledToTop() {
				const node = scrollRef.current;
				if (!node) return false;
				return getScrollPos(node) === 0;
			},
			isScrolledToBottom() {
				const node = scrollRef.current;
				if (!node) return false;
				const scrollPos = getScrollPos(node);
				const maxScroll = getMaxScroll(node);
				return scrollPos >= maxScroll;
			},
			getDistanceFromTop() {
				const node = scrollRef.current;
				if (!node) return 0;
				return Math.max(0, getScrollPos(node));
			},
			getDistanceFromBottom() {
				const node = scrollRef.current;
				if (!node) return 0;
				const scrollPos = getScrollPos(node);
				const maxScroll = getMaxScroll(node);
				return Math.max(0, maxScroll - scrollPos);
			},
		}),
		[orientation, cancelAnimation, clampToRange, getMaxScroll, getScrollPos, setScrollPos, springScrollTo],
	);
	const handleScroll = useCallback(
		(event: UIEvent<HTMLDivElement>) => {
			if (fade) {
				setIsScrolling(true);
				window.clearTimeout(scrollTimeoutRef.current);
				scrollTimeoutRef.current = window.setTimeout(() => {
					setIsScrolling(false);
				}, 1000);
			}
			refreshThumbState();
			onScroll?.(event);
		},
		[fade, onScroll, refreshThumbState],
	);
	const handleScrollerWheel = useCallback(
		(event: ReactWheelEvent<HTMLDivElement>) => {
			cancelProgrammaticScrollForUserIntent();
			onWheel?.(event);
		},
		[cancelProgrammaticScrollForUserIntent, onWheel],
	);
	const handleScrollerTouchStart = useCallback(
		(event: ReactTouchEvent<HTMLDivElement>) => {
			cancelProgrammaticScrollForUserIntent();
			onTouchStart?.(event);
		},
		[cancelProgrammaticScrollForUserIntent, onTouchStart],
	);
	const handleScrollerKeyDown = useCallback(
		(event: ReactKeyboardEvent<HTMLDivElement>) => {
			if (shouldTreatKeyAsScrollIntent(event)) {
				cancelProgrammaticScrollForUserIntent();
			}
			onKeyDown?.(event);
		},
		[cancelProgrammaticScrollForUserIntent, onKeyDown],
	);
	const handleMiddleMouseAutoscroll = useCallback(
		(event: ReactMouseEvent<HTMLDivElement>) => {
			onMouseDown?.(event);
			if (
				event.button === MIDDLE_MOUSE_BUTTON &&
				isSameMouseDownEvent(lastMiddleMouseAutoscrollStopEventRef.current, event.nativeEvent)
			) {
				event.preventDefault();
				return;
			}
			if (event.button === MIDDLE_MOUSE_BUTTON && middleMouseAutoscrollRef.current) {
				event.preventDefault();
				lastMiddleMouseAutoscrollStopEventRef.current = getMiddleMouseStopEvent(event.nativeEvent);
				stopMiddleMouseAutoscroll();
				return;
			}
			if (event.defaultPrevented || event.button !== MIDDLE_MOUSE_BUTTON) {
				return;
			}
			const node = scrollRef.current;
			if (
				overflow === 'hidden' ||
				targetMatches(event.target, AUTOSCROLL_IGNORED_TARGET_SELECTOR) ||
				!node ||
				getMaxScroll(node) <= 0
			) {
				if (!targetMatches(event.target, AUTOSCROLL_NATIVE_DEFAULT_TARGET_SELECTOR)) {
					event.preventDefault();
				}
				return;
			}
			event.preventDefault();
			cancelAnimation();
			stopMiddleMouseAutoscroll();
			const state: MiddleMouseAutoscrollState = {
				node,
				originClientX: event.clientX,
				originClientY: event.clientY,
				lastClientX: event.clientX,
				lastClientY: event.clientY,
				lastFrameTime: performance.now(),
				rafId: null,
				isMiddleButtonDown: true,
				stopOnMiddleMouseUp: false,
			};
			middleMouseAutoscrollRef.current = state;
			const handleMouseMove = (mouseEvent: MouseEvent) => {
				state.lastClientX = mouseEvent.clientX;
				state.lastClientY = mouseEvent.clientY;
				if (state.isMiddleButtonDown) {
					const deltaX = mouseEvent.clientX - state.originClientX;
					const deltaY = mouseEvent.clientY - state.originClientY;
					if (Math.hypot(deltaX, deltaY) > AUTOSCROLL_HOLD_RELEASE_THRESHOLD_PX) {
						state.stopOnMiddleMouseUp = true;
					}
				}
			};
			const handleCancel = (mouseEvent?: MouseEvent) => {
				if (mouseEvent?.button === MIDDLE_MOUSE_BUTTON) {
					mouseEvent.preventDefault();
					lastMiddleMouseAutoscrollStopEventRef.current = getMiddleMouseStopEvent(mouseEvent);
				}
				stopMiddleMouseAutoscroll();
			};
			const handleMouseDown = (mouseEvent: MouseEvent) => {
				handleCancel(mouseEvent);
			};
			const handleMouseUp = (mouseEvent: MouseEvent) => {
				if (mouseEvent.button !== MIDDLE_MOUSE_BUTTON) {
					return;
				}
				state.isMiddleButtonDown = false;
				if (state.stopOnMiddleMouseUp) {
					mouseEvent.preventDefault();
					handleCancel();
				}
			};
			const handleWheel = () => {
				handleCancel();
			};
			const handleBlur = () => {
				handleCancel();
			};
			const handleKeyDown = (keyEvent: KeyboardEvent) => {
				if (keyEvent.key === 'Escape') {
					handleCancel();
				}
			};
			state.cleanup = () => {
				window.removeEventListener('mousemove', handleMouseMove, true);
				window.removeEventListener('mousedown', handleMouseDown, true);
				window.removeEventListener('mouseup', handleMouseUp, true);
				window.removeEventListener('wheel', handleWheel, true);
				window.removeEventListener('blur', handleBlur, true);
				document.removeEventListener('keydown', handleKeyDown, true);
			};
			const step = (frameTime: number) => {
				if (middleMouseAutoscrollRef.current !== state) {
					return;
				}
				const dt = Math.max(0, Math.min(0.05, (frameTime - state.lastFrameTime) / 1000));
				state.lastFrameTime = frameTime;
				const pointerDelta =
					orientation === 'vertical'
						? state.lastClientY - state.originClientY
						: state.lastClientX - state.originClientX;
				const velocity = getAutoscrollVelocity(pointerDelta);
				if (velocity !== 0) {
					const nextScrollPosition = clampToRange(state.node, getScrollPos(state.node) + velocity * dt);
					setScrollPos(state.node, nextScrollPosition);
				}
				state.rafId = requestAnimationFrame(step);
			};
			setMiddleMouseAutoscrollCursor(orientation);
			window.addEventListener('mousemove', handleMouseMove, true);
			window.addEventListener('mousedown', handleMouseDown, true);
			window.addEventListener('mouseup', handleMouseUp, true);
			window.addEventListener('wheel', handleWheel, true);
			window.addEventListener('blur', handleBlur, true);
			document.addEventListener('keydown', handleKeyDown, true);
			state.rafId = requestAnimationFrame(step);
		},
		[
			cancelAnimation,
			clampToRange,
			getMaxScroll,
			getScrollPos,
			onMouseDown,
			orientation,
			overflow,
			setScrollPos,
			stopMiddleMouseAutoscroll,
		],
	);
	const handleTrackWheel = useCallback(
		(event: ReactWheelEvent<HTMLDivElement>) => {
			const scrollElement = scrollRef.current;
			if (!scrollElement) return;
			cancelProgrammaticScrollForUserIntent();
			if (orientation === 'vertical') {
				scrollElement.scrollTop += event.deltaY;
			} else {
				scrollElement.scrollLeft += event.deltaX || event.deltaY;
			}
		},
		[cancelProgrammaticScrollForUserIntent, orientation, scrollRef],
	);
	const handleTrackPointerDown = useCallback(
		(event: ReactPointerEvent<HTMLDivElement>) => {
			cancelProgrammaticScrollForUserIntent();
			onTrackPointerDown(event);
		},
		[cancelProgrammaticScrollForUserIntent, onTrackPointerDown],
	);
	const handleThumbPointerDown = useCallback(
		(event: ReactPointerEvent<HTMLDivElement>) => {
			cancelProgrammaticScrollForUserIntent();
			onThumbPointerDown(event);
		},
		[cancelProgrammaticScrollForUserIntent, onThumbPointerDown],
	);
	const handleMouseEnter = useCallback(() => {
		setIsHovered(canUseWindowFocusedHoverControls());
	}, []);
	const handleMouseLeaveInternal = useCallback(
		(event: ReactMouseEvent<HTMLDivElement>) => {
			setIsHovered(false);
			onMouseLeave?.(event);
		},
		[onMouseLeave],
	);
	useEffect(() => {
		const syncHoverWithWindowFocus = () => {
			setIsHovered(canUseWindowFocusedHoverControls() && Boolean(rootRef.current?.matches(':hover')));
		};
		const unsubscribe = subscribeWindowHoverControlsChange(syncHoverWithWindowFocus);
		window.addEventListener('blur', syncHoverWithWindowFocus);
		return () => {
			unsubscribe();
			window.removeEventListener('blur', syncHoverWithWindowFocus);
		};
	}, []);
	const scrollerStyle: CSSProperties = {
		...style,
		overflowY: orientation === 'vertical' ? overflow : 'hidden',
		overflowX: orientation === 'horizontal' ? overflow : 'hidden',
	};
	const shouldReserveTrackSpace = scrollbarTrackMode === 'reserve';
	const containerClassName = clsx(styles.scrollerWrap, {
		[styles.horizontal]: orientation === 'horizontal',
		[styles.regular]: scrollbar === 'regular',
		[styles.scrollbarReserve]: shouldReserveTrackSpace,
	});
	const scrollerClassName = clsx(styles.scroller, className, {
		[styles.regular]: scrollbar === 'regular',
		[styles.dragging]: isDragging,
	});
	const hasTrack = showTrack && thumbState.hasTrack;
	const isTrackVisible =
		hasTrack && (!hideThumbWhenWindowBlurred || !isWindowBlurred) && (!fade || isHovered || isScrolling || isDragging);
	return (
		<div
			role="group"
			className={containerClassName}
			ref={rootRef}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeaveInternal}
		>
			<FocusRingScope containerRef={rootRef}>
				<div
					ref={scrollRef}
					role="group"
					className={scrollerClassName}
					style={scrollerStyle}
					dir={dir}
					onScroll={handleScroll}
					onKeyDown={handleScrollerKeyDown}
					onMouseDown={handleMiddleMouseAutoscroll}
					onTouchStart={handleScrollerTouchStart}
					onWheel={handleScrollerWheel}
					{...rest}
				>
					<div className={clsx(styles.scrollerChildren, contentClassName)} ref={contentRef}>
						{children}
					</div>
				</div>
				<ScrollerTrack
					orientation={orientation}
					scrollbar={scrollbar}
					hasTrack={hasTrack}
					isVisible={isTrackVisible}
					isDragging={isDragging}
					thumbOffset={thumbState.thumbOffset}
					thumbSize={thumbState.thumbSize}
					onTrackPointerDown={handleTrackPointerDown}
					onThumbPointerDown={handleThumbPointerDown}
					onWheel={handleTrackWheel}
				/>
			</FocusRingScope>
		</div>
	);
});
Scroller.displayName = 'Scroller';
